import { type CustomTooltipProps } from "ag-grid-react"
import { Card, CardContent, CardHeader, CardTitle } from "~/components/reusable/Card"
import PreviousForecastComparisonGraph from "~/components/reusable/graphs/PreviousForecastComparisonGraph"
import HeaderTooltip from "./HeaderTooltip"
import { formatDate } from "~/utils/datetime"
import { formatForecast } from "~/utils/formatting"
import { roundNumber } from "~/utils/helpers"

/**
 * Value supplied via `tooltip` for forecast-change cells. Carries the
 * pre-computed period totals so the tooltip can explain how the change number was derived
 * without recomputing forecasts itself.
 */
export type ForecastChangeTooltipValue = {
  periodLabel: string
  periodStart: Date
  periodEnd: Date
  comparisonDate: Date | null
  current: number
  comparison: number
  change: number
  pct: number
  variantIds: string[]
  netMode: boolean
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-6">
      <span className="paragraph-sm text-fg-subtle">{label}</span>
      <span className="paragraph-sm tabular-nums">{value}</span>
    </div>
  )
}

export default function ForecastChangeTooltip(
  props: CustomTooltipProps<unknown, ForecastChangeTooltipValue>,
) {
  if (props.location === "header") {
    return <HeaderTooltip {...props} />
  }
  if (props.location !== "cell" || !props.value) return null

  const {
    periodLabel,
    periodStart,
    periodEnd,
    comparisonDate,
    current,
    comparison,
    change,
    pct,
    variantIds,
    netMode,
  } = props.value

  const changeSign = change > 0 ? "+" : ""
  const pctRounded = roundNumber(pct, 0)
  const pctSign = pctRounded > 0 ? "+" : ""

  return (
    <Card className="w-[420px]">
      <CardHeader>
        <CardTitle>
          {periodLabel}
          {netMode ? " (net)" : ""}
        </CardTitle>
        <div className="paragraph-sm text-fg-subtle">
          {formatDate(periodStart)} – {formatDate(periodEnd)}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <Row label="Current forecast" value={formatForecast(current, 0)} />
          <Row
            label={
              comparisonDate ? `Comparison (${formatDate(comparisonDate)})` : "Comparison forecast"
            }
            value={formatForecast(comparison, 0)}
          />
          <div className="flex items-center justify-between gap-6 border-t border-base pt-1 mt-1">
            <span className="label-base">Change</span>
            <span className="label-base tabular-nums">
              {changeSign}
              {formatForecast(change, 0)} ({pctSign}
              {pctRounded}%)
            </span>
          </div>
        </div>
        {comparisonDate && variantIds.length > 0 && (
          <PreviousForecastComparisonGraph
            variantIds={variantIds}
            forecastDate={comparisonDate}
            startDate={periodStart}
            endDate={periodEnd}
            valueMode={netMode ? "net" : "gross"}
            bare
          />
        )}
      </CardContent>
    </Card>
  )
}
